import React, { useState, useEffect } from "react";
import {
  Workflow,
  Upload,
  Sparkles,
  FolderCheck,
  Eye,
  Check,
  CircleCheckBig,
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
} from "lucide-react";

export default function DocumentJourney() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isPulsing, setIsPulsing] = useState(false);

  const steps = [
    {
      number: 1,
      title: "Upload to Task",
      description: "Document uploaded to 'Client Proposal' task",
      details: "You attach Proposal.pdf while working on your task",
      icon: Upload,
      position: "right",
    },
    {
      number: 2,
      title: "Auto-Detection",
      description: "System recognizes the document",
      details: "Simpliflow instantly identifies the file and its context",
      icon: Sparkles,
      position: "left",
    },
    {
      number: 3,
      title: "Smart Categorization",
      description: "Automatically placed in 'Clients' category",
      details: "File is routed to the correct category based on task type",
      icon: FolderCheck,
      position: "right",
    },
    {
      number: 4,
      title: "Task Linking",
      description: "Connected to original task",
      details: "'From Task' badge shows the connection",
      icon: Workflow,
      position: "left",
    },
    {
      number: 5,
      title: "Ready to Share",
      description: "Available for client visibility toggle",
      details: "Control whether clients see it with one click",
      icon: Eye,
      position: "right",
    },
  ];

  // Auto-play animation
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % steps.length);
        // Trigger pulse effect
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), 500);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, steps.length]);

  const handleStepClick = (index) => {
    setCurrentStep(index);
    setIsPulsing(true);
    setTimeout(() => setIsPulsing(false), 500);
  };

  const handleNextStep = () => {
    setCurrentStep((prev) => (prev + 1) % steps.length);
    setIsPulsing(true);
    setTimeout(() => setIsPulsing(false), 500);
  };

  const handlePreviousStep = () => {
    setCurrentStep((prev) => (prev - 1 + steps.length) % steps.length);
    setIsPulsing(true);
    setTimeout(() => setIsPulsing(false), 500);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setIsPulsing(true);
    setTimeout(() => setIsPulsing(false), 500);
  };

  const handleToggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  const renderStepCard = (step, index) => {
    const Icon = step.icon;
    const isActive = index === currentStep;
    const isCompleted = index < currentStep;

    return (
      <div
        key={index}
        className={`flex items-center gap-8 md:flex-row  ${
          step.position === "left" ? "md:flex-row-reverse" : ""
        }`}
      >
        {/* Step Card */}
        <div
          className={`flex-1 ${
            step.position === "left" ? "md:text-left" : "md:text-right"
          }`}
        >
          <div
            onClick={() => handleStepClick(index)}
            className={`bg-gradient-to-br from-gray-800/90 to-gray-700/80 backdrop-blur-sm rounded-2xl 
                             Card_Padding_Radius shadow-xl border-2 cursor-pointer transition-all duration-500 ${
                               isActive
                                 ? "border-cyan-500/50 shadow-cyan-500/20 scale-[1.02]"
                                 : isCompleted
                                   ? "border-cyan-400/30 hover:border-cyan-400/50"
                                   : "border-gray-600/50 hover:border-gray-500/50"
                             }`}
          >
            <div
              className={`flex items-start gap-4 ${
                step.position === "left"
                  ? ""
                  : "md:flex-row-reverse md:text-right"
              }`}
            >
              <div
                className={`IconBox_Responsive rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg transition-all duration-300 ${
                  isActive
                    ? "bg-gradient-to-br from-cyan-500 to-cyan-600 animate-pulse"
                    : isCompleted
                      ? "bg-gradient-to-br from-cyan-400/80 to-cyan-500/80"
                      : "bg-gradient-to-br from-gray-700 to-gray-600"
                }`}
              >
                <Icon className="IconBox_Icon text-white" />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`text-sm font-medium transition-colors ${
                      isActive || isCompleted
                        ? "text-cyan-300"
                        : "text-gray-400"
                    }`}
                  >
                    Step {step.number}
                  </span>
                  {isCompleted && <Check className="w-4 h-4 text-green-400" />}
                </div>

                <h3
                  className={`Step_Title mb-2 transition-colors ${
                    isActive || isCompleted ? "text-white" : "text-gray-300"
                  }`}
                >
                  {step.title}
                </h3>

                <p
                  className={`Step_Desc text-gray-300 mb-3 transition-colors ${
                    !isActive && !isCompleted ? "opacity-70" : ""
                  }`}
                >
                  {step.description}
                </p>

                <p
                  className={`Step_Details italic transition-colors ${
                    isActive
                      ? "text-cyan-200/80"
                      : isCompleted
                        ? "text-cyan-100/60"
                        : "text-gray-400"
                  }`}
                >
                  {step.details}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Dot (Desktop only) */}
        <div className="relative z-10 hidden md:block">
          <div
            className={`w-6 h-6 rounded-full transition-all duration-300 ${
              isActive
                ? "bg-cyan-500/80 scale-125"
                : isCompleted
                  ? "bg-cyan-400/60"
                  : "bg-gray-500/40"
            }`}
            style={{
              boxShadow: isActive
                ? "0 0 20px rgba(6, 182, 212, 0.8)"
                : isCompleted
                  ? "0 0 10px rgba(6, 182, 212, 0.4)"
                  : "0 0 5px rgba(255, 255, 255, 0.1)",
            }}
          >
            {isActive && (
              <div className="absolute inset-0 rounded-full bg-cyan-400 animate-ping opacity-30"></div>
            )}
          </div>
        </div>

        {/* Spacer for desktop */}
        <div className="flex-1 hidden md:block"></div>
      </div>
    );
  };

  return (
    // bg-gradient-to-br from-gray-900 via-black to-gray-900 bg-[#010c13]
    <section className="relative bg-white  DocsSection_Spacing overflow-hidden">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: "100px 100px",
        }}
      ></div>

      {/* Animated Glow Effects */}
      {/* <div
        className={`absolute top-20 right-1/4 w-[500px] h-[500px] bg-cyan-500/20 rounded-full blur-[100px] ${
          isAutoPlaying ? "animate-pulse" : ""
        }`}
      ></div> */}
      <div
        className={`absolute bottom-20 left-1/4 w-[400px] h-[400px] bg-[#093a5b]/20 rounded-full blur-[80px] ${
          isAutoPlaying ? "animate-pulse" : ""
        }`}
        style={{ animationDelay: "1s" }}
      ></div>

      <div className="max-w-7xl mx-auto relative z-10 ">
        {/* Header */}
        <div className="text-center Section_MB">
          <div>
            {/* <div
              className="inline-flex items-center gap-2 bg-cyan-500/20 border-2 border-cyan-400/50 text-cyan-300
                          rounded-full shadow-[0_0_20px_rgba(6,182,212,0.3)] Lifecycle_Badge_Padding Lifecycle_Badge_MB"
            >
              <Workflow className="w-4 h-4" />
              <span className="Document_Journey_badge">Complete Lifecycle</span>
            </div> */}

            {/* drop-shadow-[0_0_30px_rgba(6,182,212,0.3)] */}
            <h2 className="text-black mb-6 DocJourney_Title">
              Follow the
              <br />
              <span
                // bg-gradient-to-r from-cyan-300 via-cyan-400 to-cyan-300
                //               bg-clip-text text-transparent
                className="DocJourney_TitleGradient text-[#093a5b]"
              >
                Document Journey
              </span>
            </h2>

            <p className="text-[#94A3B8] max-w-2xl mx-auto DocJourney_Desc">
              Watch how a single document flows seamlessly from task to
              organized storage, ready to share.
            </p>
          </div>
        </div>

        {/* Controls */}
        {/* <div className="flex justify-center items-center gap-4 mb-8">
                    <button
                        onClick={handlePreviousStep}
                        className="px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-gray-300 hover:text-white hover:border-cyan-500/50 transition-colors"
                    >
                        Previous
                    </button>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleToggleAutoPlay}
                            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${isAutoPlaying
                                ? 'bg-cyan-500/20 border border-cyan-500/50 text-cyan-300'
                                : 'bg-gray-800/50 border border-gray-700 text-gray-300 hover:text-white'
                                } transition-colors`}
                        >
                            {isAutoPlaying ? (
                                <>
                                    <Pause className="w-4 h-4" />
                                    Pause
                                </>
                            ) : (
                                <>
                                    <Play className="w-4 h-4" />
                                    Play
                                </>
                            )}
                        </button>

                        <button
                            onClick={handleReset}
                            className="px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-gray-300 hover:text-white hover:border-cyan-500/50 transition-colors flex items-center gap-2"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Reset
                        </button>
                    </div>

                    <button
                        onClick={handleNextStep}
                        className="px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-gray-300 hover:text-white hover:border-cyan-500/50 transition-colors"
                    >
                        Next
                    </button>
                </div> */}
        {/* <div className="controls-wrapper mb-8">
                    <button
                        onClick={handlePreviousStep}
                        className="control-btn prev"
                    >
                        Previous
                    </button>

                    <button
                        onClick={handleToggleAutoPlay}
                        className={`control-btn autoplay ${isAutoPlaying ? 'active' : ''}`}
                    >
                        {isAutoPlaying ? (
                            <>
                                <Pause className="w-4 h-4" /> Pause
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4" /> Play
                            </>
                        )}
                    </button>

                    <button
                        onClick={handleReset}
                        className="control-btn reset"
                    >
                        <RotateCcw className="w-4 h-4" /> Reset
                    </button>

                    <button
                        onClick={handleNextStep}
                        className="control-btn next"
                    >
                        Next
                    </button>
                </div> */}
        {/* Progress Indicator */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-cyan-300">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm text-gray-400">
              {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="h-2 bg-gray-800/50 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 transition-all duration-500"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Timeline Line (Desktop only) */}
          <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-cyan-400/50 via-cyan-500/80 to-cyan-400/50 hidden md:block shadow-[0_0_10px_rgba(6,182,212,0.5)] transform -translate-x-1/2"></div>

          {/* Steps */}
          <div className="space-y-16">
            {steps.map((step, index) => renderStepCard(step, index))}
          </div>
        </div>

        {/* Completion Banner */}
        <div
          // bg-gradient-to-r from-cyan-500/30 via-cyan-400/20 to-cyan-500/30
          //          border-2 border-cyan-400/60
          className={`Lifecycle_FinishBox bg-[#093a5b] text-center relative overflow-hidden backdrop-blur-sm shadow-[0_0_40px_rgba(6,182,212,0.3)]
                  transition-all duration-500 ${
                    currentStep === steps.length - 1
                      ? "opacity-100 scale-100"
                      : "opacity-70 scale-95"
                  }`}
        >
          {/* Animated Background Effects */}
          <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500/20 via-cyan-400/10 to-transparent blur-3xl rounded-3xl"></div>
          <div className="absolute top-0 left-1/2 w-[3px] bg-gradient-to-b from-cyan-400 to-cyan-500 origin-top shadow-[0_0_10px_rgba(6,182,212,0.5)] transform -translate-x-1/2 h-24"></div>

          {/* Content */}
          <div className="relative z-10">
            <div
              className={`Lifecycle_SuccessIcon mx-auto mb-6 bg-cyan-500/30 backdrop-blur-sm rounded-full border-4 border-cyan-400 flex items-center justify-center shadow-[0_0_30px_rgba(6,182,212,0.6)] transition-all duration-500 ${
                currentStep === steps.length - 1 ? "scale-100" : "scale-90"
              }`}
            >
              <CircleCheckBig className="Lifecycle_SuccessIcon__icon text-cyan-300" />
            </div>

            <h3 className="Lifecycle_ResultTitle  text-white mb-4">
              {currentStep === steps.length - 1
                ? "Document Ready!"
                : "Journey in Progress"}
            </h3>

            <p className="Lifecycle_ResultDesc  text-gray-200">
              {currentStep === steps.length - 1
                ? "Organized, linked, searchable, and ready to share with one click."
                : "Following the document through its complete lifecycle..."}
            </p>

            <div className="flex items-center justify-center gap-6 flex-wrap">
              {["Categorized", "Linked to Task", "Searchable", "Shareable"].map(
                (feature, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-2 transition-all duration-300 ${
                      index <= currentStep
                        ? "text-gray-200 opacity-100"
                        : "text-gray-400 opacity-50"
                    }`}
                  >
                    {index <= currentStep ? (
                      <Check className="FeatureStep_Icon text-cyan-300" />
                    ) : (
                      <ChevronRight className="FeatureStep_Icon text-gray-500" />
                    )}
                    <span className="FeatureStep_Text">{feature}</span>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>

        {/* Step Navigation Dots (Mobile) */}
        <div className="md:hidden flex justify-center gap-2 MobileControls_MT">
          {steps.map((_, index) => (
            <button
              key={index}
              onClick={() => handleStepClick(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentStep
                  ? "bg-cyan-500 scale-125"
                  : index < currentStep
                    ? "bg-cyan-400/60"
                    : "bg-gray-600"
              }`}
              aria-label={`Go to step ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
